import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Box,
  Button,
  TextField,
  CircularProgress,
  Alert,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Typography,
  IconButton,
  Tooltip,
} from "@mui/material";
import AddCircleRoundedIcon from "@mui/icons-material/AddCircleRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import dayjs from "dayjs";

import { allowedUsers } from "./constants";
import { getStampColor, LEAVE_COLOR} from "./constants";

import SwapHorizRoundedIcon from "@mui/icons-material/SwapHorizRounded";


//* Only Leave	From Date and To Date (as today)	dateFrom → dateTo
//* Work only	One Date (the day you clicked)	dateFrom = dateTo = that day
//* Work + Leave	One Date (the day you clicked)	dateFrom = dateTo = that day

function normalizeApiBase(raw) {
  const s = String(raw ?? "").trim().replace(/\/$/, "");
  if (!s) return "";
  if (/^https?:\/\//i.test(s)) return s;
  return s;
}

function shouldIgnoreApiMessage(msg) {
  const s = String(msg || "").trim();
  return /missing\s*\?from\s*=\s*yyyy-mm-dd\s*&\s*to\s*=\s*yyyy-mm-dd/i.test(s);
}

function makeBlankSegment() {
  return {
    isLeave: true,
    leaveCode: "",
    timeFrom: dayjs().hour(8).minute(0),
    timeTo: dayjs().hour(16).minute(0),
    isExisting:false,
    isLocked: false,
  };
}

// Turns the raw "W:08:00 ~ 16:00,L:09:00 ~ 10:00" string (and the
// "Vacation;3;Annual" status string) back into editable segments —
// same idea as EditTimeDialog's cellSegments prefill.
function parseExistingToSegments(rawTime, rawStatus, typeOptions,stampModules) {
  const raw = String(rawTime || "").trim();
  if (!raw) return [makeBlankSegment()];

  // pull the leave type NAME + approval code out of  "Vacation;3;Annual" (or "Vacation;3:Annual,2:Sick")
  let leaveTypeName = "";
  let leaveIsApproved = false;
  const statusStr = String(rawStatus || "").trim();
  if (statusStr.startsWith("Vacation")){
    const rest = statusStr.slice("Vacation;".length);
    const firstEntry = rest.split(",")[0] || "";
    const [code, ...nameParts] = firstEntry.split(":");
    leaveTypeName= nameParts.join(":").trim();
    leaveIsApproved = ["2", "3"].includes(code.trim());
  }
  // const statusParts = String(rawStatus || "").split(";");
  // if (statusParts[0]?.trim() === "Vacation") leaveTypeName = (statusParts[2] || "").trim();

  // match that name back to a real leave code from the dropdown list
  const matched = (typeOptions || []).find(
    (t) => t.name && leaveTypeName && t.name.toLowerCase() === leaveTypeName.toLowerCase()
  );
  const matchedCode = matched ? matched.code : "";

    const modules = String(stampModules || "")
    .split("|")
    .map((v) => parseInt(v.trim(), 10));
  let workIdx = 0;

  const parts = raw.split(",").map((s) => s.trim()).filter(Boolean);
  const segs = parts.map((seg) => {
    const isLeave = seg.startsWith("L:");
    const timeStr = seg.replace(/^[WL]:/, "").trim();
    const [inStr, outStr] = timeStr.split("~").map((s) => (s ? s.trim() : ""));
    const inModule = !isLeave && inStr ? modules[workIdx++] : null;
    const outModule = !isLeave && outStr ? modules[workIdx++] : null;

    return {
      isLeave,
      leaveCode: isLeave ? matchedCode : "",
      timeFrom: inStr ? dayjs(inStr, "HH:mm") : null,
      timeTo: outStr ? dayjs(outStr, "HH:mm") : null,
      isExisting: true,
      isLocked: isLeave && leaveIsApproved,
      inModule,
      outModule,
      inLocked: !isLeave && !!inStr && inModule !== 4,
      outLocked: !isLeave && !!outStr && outModule !== 4,
    };
  });

  return segs.length ? segs : [makeBlankSegment()];
}

export default function EmployeeEntryDialog({ open, onClose, empId, setError, onSave , initialDate, initialRawTime, initialRawStatus, initialStampModules }) {

  const [localError, setLocalError] = useState("");

  const [vacationTypes, setVacationTypes] = useState([]);
  const [vacLoading, setVacLoading] = useState(false);

  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);

  const [segments, setSegments] = useState([makeBlankSegment()]);
  const [remark, setRemark] = useState("");
// check if entry attendance is manually 
  const [hadManualAtOpen, setHadManualAtOpen] = useState(false);

  const [saving, setSaving] = useState(false);

  // Employee Manual Entry 
  const [manualInfo, setManualInfo] = useState({ used:0, limit:null, block:1, graceDays:null, usedDates:[]});
  const hasWork = segments.some((seg) => !seg.isLeave);
  const effectiveToDate = hasWork ? fromDate : toDate;

  useEffect(() => {
    if (!open) return;
        console.log("EED", { initialRawTime, initialRawStatus, initialStampModules });

    setLocalError("");
    const start = initialDate ? dayjs(initialDate) : null;
    setFromDate(start);
    setToDate(start);
    setSegments([makeBlankSegment()]);
    setHadManualAtOpen(false);
    setRemark("");

    const apiUrl = normalizeApiBase(localStorage.getItem("saved_sett_api_url"));
    if (!apiUrl) {
      setLocalError("Missing API URL in localStorage (saved_sett_api_url).");
      return;
    }

    setVacLoading(true);
    axios
      .get(`${apiUrl}/api/vacation-types`, { withCredentials: true })
      .then((res) => {
        const list = Array.isArray(res.data)
          ? res.data.map((r) => ({
              code: r.LeaveId ?? r.leaveid ?? r.Code,
              name: r.LeaveName ?? r.leavename ?? r.ArbStringValue ?? "",
            }))
          : [];
        setVacationTypes(list);
       
        // ── NEW: only after we have the real leave-type list can we
        // correctly match the existing leave's name back to its code
        const parsed = parseExistingToSegments(initialRawTime, initialRawStatus, list, initialStampModules);
        setSegments(parsed);
        setHadManualAtOpen(parsed.some((s) => !s.isLeave && (s.inModule === 4 || s.outModule === 4)));

      })
      .catch((error) => {
        const msg =
          error.response?.data?.message || error.response?.data?.error || "Failed to load vacation types.";
        if (shouldIgnoreApiMessage(msg)) return;
        setVacationTypes([]);
        setLocalError(msg);
        if (setError) setError(msg);
        // still prefill times even if the type list failed to load
        //setSegments(parseExistingToSegments(initialRawTime, initialRawStatus, [], initialStampModules));
        const parsed = parseExistingToSegments(initialRawTime, initialRawStatus, [], initialStampModules);
        setSegments(parsed);
        setHadManualAtOpen(parsed.some((s) => !s.isLeave && (s.inModule === 4 || s.outModule === 4)));
      })
      .finally(() => setVacLoading(false));
  }, [open, setError, initialDate, initialRawTime, initialRawStatus, initialStampModules]);


  useEffect (() => {
    if (!open || !fromDate) return;
    const resolvedEmpId =  localStorage.getItem("shared_empid") || localStorage.getItem("saved_username") || "" ;
    const apiUrl= normalizeApiBase(localStorage.getItem("saved_sett_api_url"));
    if (!resolvedEmpId || !apiUrl) return;

    axios.get(`${apiUrl}/api/attendance/manual-entry-status`, {
        params: { empId: resolvedEmpId, month:dayjs(fromDate).format("YYYY-MM") },
        withCredentials: true
      })
      .then((res) => {
        // block: 1 = refuse over the limit, 0 = allow and notify. 
        // The dialog didn’t use it before, so it always refused manual attendance over the limit, 
        // even with block = 0 (when the backend would have allowed it). 
        // Now it follows the same setting as the backend.
        setManualInfo({
          used :res.data.used ,
          limit : res.data.limit ,
          block : res.data.block ,
          graceDays : res.data.graceDays ,
          usedDates : res.data.usedDates || [],
        });
      })
      .catch((err) => console.error("Failed to load manual entry status:", err));
    }, [open, fromDate] );

    const addSegment = () => 
      setSegments((s) => {
        const lastIsLeave = s.length ? s[s.length-1].isLeave: true;
        return [
          ...s,
          {
            isLeave : lastIsLeave,
            leaveCode: "",
            timeFrom: lastIsLeave ? dayjs().hour(8).minute(0) : null,
            timeTo: lastIsLeave ? dayjs().hour(16).minute(0) : null, 
            isExisting: false,
            isLocked: false,
          },
        ];
      });

  const removeSegment = (idx) => {
  setSegments((s) => {
    const target = s[idx];
    if (target && (target.inLocked || target.outLocked)) {
      return s.map((x, i) =>
        i === idx
          ? {
              ...x,
              timeFrom: x.inLocked ? x.timeFrom : null,
              timeTo: x.outLocked ? x.timeTo : null,
              inModule: x.inLocked ? x.inModule : null,
              outModule: x.outLocked ? x.outModule : null,
            }
          : x
      );
    }
    if (s.length <= 1) {
      // return [makeBlankSegment()]; // last one left: clear it instead of removing
      // is working hour ia last one keep work on ! 
      return [{ ...makeBlankSegment(), isLeave: s[idx]?.isLeave ?? true, timeFrom: null, timeTo: null }]; // last one left: clear it instead of removing // last one left: clear it instead of removing, to let user delete the leave 
    }
    return s.filter((_, i) => i !== idx);
  });
};

  const updateSegment = (idx, patch) => {
    setSegments((s) => s.map((seg, i) => (i === idx ? { ...seg, ...patch } : seg)));
  };

   const getTimeTextColor = (seg, which) => {
    if (!seg.isExisting) return undefined;
    if (seg.isLeave) return LEAVE_COLOR;
    const mod = which === "in" ? seg.inModule : seg.outModule;
    if (mod === null || mod === undefined) return undefined;
    return getStampColor(mod);
  }; 

    const timeFieldSx = (seg, which) => {
    const color = getTimeTextColor(seg, which);
    return {
      backgroundColor: "white",
      borderRadius: 2,
      ...(color && {
        "& .MuiInputBase-root, & .MuiPickersInputBase-root": {
          color,
          fontWeight: 800,
        },
        "& .MuiInputBase-input, & .MuiPickersSectionList-root": {
          color,
          WebkitTextFillColor: color,
          fontWeight: 800,
        },
      }),
    };
  };

 const buildPayloadSegments = () => {
  return segments
    .filter((seg) => seg.timeFrom || seg.timeTo)
    .filter((seg) => !seg.isLocked) // don't resend an already-approved leave that wasn't touched
    .map((seg) => ({
      mode: seg.isLeave ? "LEAVE" : "WORK",
      leave_type: seg.isLeave ? seg.leaveCode : 0,
      time_in: seg.timeFrom ? dayjs(seg.timeFrom).format("HH:mm") : "",
      time_out: seg.timeTo ? dayjs(seg.timeTo).format("HH:mm") : "",
    }));
};


  const getLimitCheck = () => {
    const isManualTime = (seg, which) => {
      const t = which === "in" ? seg.timeFrom : seg.timeTo;
      if (!t) return false;
      if (!seg.isExisting) return true;
      const mod = which === "in" ? seg.inModule : seg.outModule;
      return mod === null || mod === undefined || mod === 4;
    };
    const sendsManualWork = segments.some(
      (seg) => !seg.isLeave && (isManualTime(seg, "in") || isManualTime(seg, "out"))
    );
    const sendsLeave = segments.some(
      (seg) => seg.isLeave && !seg.isLocked && (seg.timeFrom || seg.timeTo)
    );
    //is there a leave block that’s new (not an existing leave being shown again)? Only a new leave can be late, the same rule as the backend.
    const sendsNewLeave = segments.some(
      (seg) => seg.isLeave && !seg.isLocked && !seg.isExisting && seg.timeFrom && seg.timeTo
    );
    // the grace days from the backend.
    const grace = Number(manualInfo.graceDays ?? 0);
    const result = { lateDays: [], projected: manualInfo.used, over: false, sendsManualWork, sendsLeave, grace };
    if (!fromDate || manualInfo.limit == null) return result;

    // check employee entry manual for the days not allowed to enter any mnaual records incase of reach limit 
    const monthKey = dayjs(fromDate).format("YYYY-MM");
    const usedSet = new Set((manualInfo.usedDates || []).map((d) => dayjs(d).format("YYYY-MM-DD")));
    const today = dayjs().startOf("day");

    if (sendsNewLeave && manualInfo.graceDays != null) {
      let d = dayjs(fromDate).startOf("day");
      const end = dayjs(effectiveToDate || fromDate).startOf("day");
      // goes through each leave day of the request. A day is late if today
      //  is after day + grace. Only days in the same month as the dialog’s
      //  counter are checked (the counter shows one month).
      while (!d.isAfter(end, "day")) {
        if (d.format("YYYY-MM") === monthKey && today.isAfter(d.add(grace, "day"), "day")) {
          result.lateDays.push(d.format("YYYY-MM-DD"));
        }
        d = d.add(1, "day");
      }
    }
    // late days plus the manual attendance day, joined in a Set, so one day counts once (your rule 6).
    const newDays = new Set(result.lateDays);
    if (sendsManualWork) newDays.add(dayjs(fromDate).format("YYYY-MM-DD"));
    // only the days not already counted this month (usedDates)
    const countedNew = [...newDays].filter((d) => !usedSet.has(d)).length;
    // projected: the counter after saving. over: true if it would pass the limit
    result.projected = manualInfo.used + countedNew;
    result.over = result.projected > manualInfo.limit;
    return result;
  };


const validate = () => {
  if (!fromDate || !effectiveToDate) return hasWork ? "Please select the date." : "Please select From Date and To Date.";
  if (dayjs(effectiveToDate).isBefore(dayjs(fromDate), "day")) return "To Date cannot be before From Date.";

    for (const seg of segments) {
      if (seg.isLocked) continue; // already-approved leave — not being touched, don't validate it
      const hasAny = seg.timeFrom || seg.timeTo;
      if (!hasAny) continue; // fully empty block — ignored, not an error
      if (seg.isLeave) {
        // Leave: BOTH times are required
        if (!seg.timeFrom || !seg.timeTo) return "Each Leave block needs both a start and an end time.";
        if (dayjs(seg.timeTo).isBefore(dayjs(seg.timeFrom))) return "Time To must be after Time From.";
        if (!seg.leaveCode) return "Please select a leave type for each Leave block.";
      } else {
        // Work: only ONE of the two times needs to be filled
        if (seg.timeFrom && seg.timeTo && dayjs(seg.timeTo).isBefore(dayjs(seg.timeFrom))) {
          return "Time To must be after Time From.";
        }
      }
  }
  //*overlap leave working !! same as edititmedialog
  //toMin / fmt: turn a time into minutes (for comparing) and into "HH:mm" (for the message
  const toMin = (d) => (d ? dayjs(d).hour() * 60 + dayjs(d).minute() : null);
  const fmt = (d) => dayjs(d).format("HH:mm");
  const ranges = segments
    .map((seg) => ({ isLeave: seg.isLeave, start: toMin(seg.timeFrom), end: toMin(seg.timeTo), seg }))
    .filter((r) => r.start !== null && r.end !== null && r.end > r.start);
  const workRanges = ranges.filter((r) => !r.isLeave);
  const leaveRanges = ranges.filter((r) => r.isLeave);
// Two ranges overlap when a.start < b.end && b.start < a.end
  for (const lv of leaveRanges) {
    if (workRanges.some((w) => lv.start < w.end && w.start < lv.end)) {
      return `❌ Leave ${fmt(lv.seg.timeFrom)}~${fmt(lv.seg.timeTo)} overlaps a work segment. You must split: e.g. Work 08:00~09:00 + Leave 09:00~10:00 + Work 10:00~16:00`;
    }
  }
//two Leaves may not overlap each other.
  for (let i = 0; i < leaveRanges.length; i++) {
    for (let j = i + 1; j < leaveRanges.length; j++) {
      const a = leaveRanges[i];
      const b = leaveRanges[j];
      if (a.start < b.end && b.start < a.end) {
        return `Leave segments overlap: ${fmt(a.seg.timeFrom)}~${fmt(a.seg.timeTo)} and ${fmt(b.seg.timeFrom)}~${fmt(b.seg.timeTo)}`;
      }
    }
  }

  // const real = buildPayloadSegments();
  // if (!real.length) return "Please add at least one time block.";

  //* when limit is reached !
  // const isManualTime = (seg, which) => {
  //   const t = which === "in" ? seg.timeFrom : seg.timeTo;
  //   if (!t) return false;
  //   if (!seg.isExisting) return true;
  //   const mod = which === "in" ? seg.inModule : seg.outModule;
  //   return mod === null || mod === undefined || mod === 4;
  // };

  // // check employee entry manual for the days not allowed to enter any mnaual records incase of reach limit 
  // const dayKey = fromDate ? dayjs(fromDate).format("YYYY-MM-DD") : "";
  // const dayAlreadyUsed = (manualInfo.usedDates || []).some(
  //   (d) => dayjs(d).format("YYYY-MM-DD") === dayKey
  // );
  // const limitReached =
  //   manualInfo.limit != null && manualInfo.used >= manualInfo.limit && !dayAlreadyUsed;


  // const sendsManualWork = segments.some(
  //   (seg) => !seg.isLeave && (isManualTime(seg, "in") || isManualTime(seg, "out"))
  // );
  // const sendsLeave = segments.some(
  //   (seg) => seg.isLeave && !seg.isLocked && (seg.timeFrom || seg.timeTo)
  // );


  // if (limitReached && sendsManualWork) {
  //   if (sendsLeave) {
  //     return `You have reached your manual entry limit (${manualInfo.used}/${manualInfo.limit} days this month). You can still save your leave, but first delete the manual attendance times from this day.`;
  //   }
  //   return `Manual entry limit reached (${manualInfo.used}/${manualInfo.limit} days this month)`;
  // }

//*   Late leave + manual attendance: both count; contact HR.
//* Late leave only: “Your leave for 27/10/2026 (deadline 29/10/2026) is late...”
//* Manual attendance + on-time leave: your existing message (delete the manual times, then save the leave).
//* Manual attendance only: your existing message.
//* With block = 0, nothing is refused here; the warning in 6e is shown instead.

  //* when limit is reached !
  const lc = getLimitCheck();
  if (lc.over && Number(manualInfo.block) === 1) {
    const lateTxt = lc.lateDays
      .map((d) => `${dayjs(d).format("DD/MM/YYYY")} (deadline ${dayjs(d).add(lc.grace, "day").format("DD/MM/YYYY")})`)
      .join(", ");
    if (lc.lateDays.length && lc.sendsManualWork) {
      return `Manual entry limit reached (${manualInfo.used}/${manualInfo.limit} days this month). Your manual attendance and your late leave for ${lateTxt} both count as manual entries. Please contact HR.`;
    }
    if (lc.lateDays.length) {
      return `Your leave for ${lateTxt} is late. Late leaves count as manual entries, and you have reached your limit (${manualInfo.used}/${manualInfo.limit} days this month). Please contact HR to add it.`;
    }
    if (lc.sendsLeave) {
      return `You have reached your manual entry limit (${manualInfo.used}/${manualInfo.limit} days this month). You can still save your leave, but first delete the manual attendance times from this day.`;
    }
    return `Manual entry limit reached (${manualInfo.used}/${manualInfo.limit} days this month)`;
  }

  return "";

};

// to jump on time out when enter is clicked !! 
  const dialogContentRef = useRef(null);

  const focusField = (idx, field) => {
    const root = dialogContentRef.current;
    if (!root) return;
    const el = root.querySelector(`input[data-seg-idx="${idx}"][data-field="${field}"]`);
    if (el && !el.disabled) {
      el.focus();
      el.select?.();
    }
  };

  const handleSubmit = async () => {
  // ✅ same resolution order as VacationDialog — read from localStorage,
  // don't rely on empId being passed in as a prop
  const resolvedEmpId = localStorage.getItem("shared_empid") || localStorage.getItem("saved_sett_mobilenumber") || "";

  const err = validate();
  if (err) {
    setLocalError(err);
    return;
  }

  const apiUrl = normalizeApiBase(localStorage.getItem("saved_sett_api_url"));
  if (!apiUrl) {
    setLocalError("Missing API URL in localStorage (saved_sett_api_url).");
    return;
  }
  if (!resolvedEmpId) {
    setLocalError("Missing empId.");
    return;
  }

  setSaving(true);
  setLocalError("");

  try {
    await axios.post(
      `${apiUrl}/api/attendance/employee-entry`,
      {
        empId: Number(resolvedEmpId) || resolvedEmpId,
        dateFrom: dayjs(fromDate).format("YYYY-MM-DD"),
        dateTo: dayjs(effectiveToDate).format("YYYY-MM-DD"),
        segments: buildPayloadSegments(),
        comment: remark,
        hrUserIds: allowedUsers,
      },
      { withCredentials: true }
    );

    if (onSave) onSave();
    if (onClose) onClose();
  } catch (error) {
    const msg = error?.response?.data?.error || error?.response?.data?.message || "Failed to save your request.";
    if (shouldIgnoreApiMessage(msg)) return;
    setLocalError(msg);
    if (setError) setError(msg);
  } finally {
    setSaving(false);
  }
};

   const limitCheck = getLimitCheck();

    return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 4, backgroundColor: "#f1fdfb", boxShadow: 6, px: { xs: 1, sm: 2 }, py: { xs: 1, sm: 1.5 } } }}
    >
      <DialogTitle sx={{ fontWeight: "bold", color: "#004d40", textAlign: "center", fontSize: { xs: "1.05rem", sm: "1.2rem" }, pb: 0 }}>
        Add Attendance / Leave
      </DialogTitle>

      <DialogContent ref={dialogContentRef}>
        {localError && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2, mt: 1 }}>
            {localError}
          </Alert>
        )}

        {!localError && limitCheck.lateDays.length > 0 && manualInfo.limit != null && (
          <Alert severity="warning" sx={{ mb: 2, borderRadius: 2, mt: 1 }}>
            {`This leave is late (deadline ${dayjs(limitCheck.lateDays[0]).add(limitCheck.grace, "day").format("DD/MM/YYYY")}). Late leaves count as manual entries: ${limitCheck.projected}/${manualInfo.limit} days this month after saving.`}
            {limitCheck.over && Number(manualInfo.block) !== 1 ? " You are over the limit, so your supervisor will be notified." : ""}
          </Alert>
        )}

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <DatePicker
              label={hasWork ? "Date" : "From Date"}
              value={fromDate}
              onChange={(v) => setFromDate(v)}
              slotProps={{
                textField: { size: "small", fullWidth: true, variant: "outlined", sx: { backgroundColor: "white", borderRadius: 2 } },
              }}
            />
            {!hasWork && (
              <DatePicker
                label="To Date"
                value={toDate}
                onChange={(v) => setToDate(v)}
                slotProps={{
                  textField: { size: "small", fullWidth: true, variant: "outlined", sx: { backgroundColor: "white", borderRadius: 2 } },
                }}
              />
            )}

            <Divider sx={{ opacity: 0.4 }} />

            <Stack spacing={1.5} useFlexGap>
              {segments.map((seg, idx) => (
                <Box
                  key={idx}
                  sx={{
                    p: { xs: 1, sm: 1.5 },
                    borderRadius: 2,
                    backgroundColor: "white",
                    border: "1px solid #b2dfdb",
                  }}
                >
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={seg.isLeave}
                          //disabled={seg.isLocked}
                          //Leave/Work Switch: can’t switch a locked block
                          disabled={seg.isLocked || seg.inLocked || seg.outLocked}
                          onChange={(e) =>{
                            const isLeave = e.target.checked;
                           updateSegment(idx, {
                             isLeave,
                             leaveCode: "",
                             timeFrom:isLeave && !seg.timeFrom ? dayjs().hour(8).minute(0) : null, 
                             timeTo: isLeave && !seg.timeTo ? dayjs().hour(16).minute(0) : null,
                             isExisting: false,
                            });
                          }}
                        />
                      }
                      label={
                        <Typography sx={{ fontWeight: 700, color: seg.isLeave ? "#ad1457" : "#00796b", fontSize: 13 }}>
                          {seg.isLeave ? "Leave" : "Work"}
                        </Typography>
                      }
                    />

                    <Tooltip title={seg.isLocked ? "Already approved — can't be removed here" : (seg.inLocked || seg.outLocked) ? "Removes only your manual times — GPS, machine and HR times stay" : "Remove this block"}>
                      <span>
                        <IconButton size="small" onClick={() => removeSegment(idx)} disabled={seg.isLocked}>
                          <DeleteRoundedIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>

                  {seg.isLeave && (
                    <FormControl size="small" fullWidth disabled={seg.isLocked} sx={{ mb: 1.2, backgroundColor: "white", borderRadius: 2 }}>
                      <InputLabel>Vacation Type</InputLabel>
                      <Select
                        value={seg.leaveCode}
                        label="Vacation Type"
                        onChange={(e) => updateSegment(idx, { leaveCode: e.target.value })}
                        endAdornment={vacLoading ? <CircularProgress size={16} sx={{ mr: 2 }} /> : null}
                      >
                        {vacationTypes.map((t) => (
                          <MenuItem key={t.code} value={t.code}>
                            {t.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}

                  <Stack direction={{xs: "column", sm:"row"}} spacing={{ xs: 0.5, sm: 1.2 }} useFlexGap>
                    <TimePicker
                      label="Time From"
                      value={seg.timeFrom}
                      onChange={(v) => updateSegment(idx, { timeFrom: v, inModule: null })}
                      ampm={seg.isLeave}
                      disabled={seg.isLocked || seg.inLocked}
                      slotProps={{
                        textField: {
                          size: "small",
                          fullWidth: true,
                          variant: "outlined",
                          sx: timeFieldSx(seg, "in"),
                          inputProps: {
                            "data-seg-idx": idx,
                            "data-field": "in",
                          },
                          onKeyDown: (e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              e.stopPropagation();
                              focusField(idx, "out");
                            }
                          },
                        },
                      }}
                    />
               {/* swap time in time out  */}
                    <Tooltip title="Swap Time From / Time To">
                      <span style={{ alignSelf: "center" }}>
                        <IconButton
                          size="small"
                          onClick={() =>
                            updateSegment(idx, {
                              timeFrom: seg.timeTo,
                              timeTo: seg.timeFrom,
                              inModule: seg.outModule,
                              outModule: seg.inModule,
                              inLocked: seg.outLocked,
                              outLocked: seg.inLocked,
                            })
                          }
                          disabled={seg.isLocked || (!seg.timeFrom && !seg.timeTo)}
                          sx={{ color: "#00796b", p: { xs: 0.25, sm: 1 }, transform: { xs: "rotate(90deg)", sm: "none" } }}
                        >
                          <SwapHorizRoundedIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>

                    <TimePicker
                      label="Time To"
                      value={seg.timeTo}
                      onChange={(v) => updateSegment(idx, { timeTo: v, outModule: null })}
                      ampm={seg.isLeave}
                      disabled={seg.isLocked || seg.outLocked}
                      slotProps={{
                        textField: {
                          size: "small",
                          fullWidth: true,
                          variant: "outlined",
                          sx: timeFieldSx(seg, "out"),
                          inputProps: {
                            "data-seg-idx": idx,
                            "data-field": "out",
                          },
                          onKeyDown: (e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              e.stopPropagation();
                              const hasNext = idx + 1 < segments.length;
                              if (hasNext) {
                                focusField(idx + 1, "in");
                              } else {
                                document.activeElement?.blur?.();
                              }
                            }
                          },
                        },
                      }}
                    />
                  </Stack>
                </Box>
              ))}
            </Stack>

            <Button
              onClick={addSegment}
              startIcon={<AddCircleRoundedIcon />}
              sx={{ color: "#00796b", fontWeight: "bold", alignSelf: "flex-start" }}
            >
              Add another time block
            </Button>

            <Divider sx={{ opacity: 0.4 }} />

            <TextField
              label="Remark"
              value={remark}
              onChange={(e) => setRemark(e.target.value)}
              fullWidth
              multiline
              rows={3}
              variant="outlined"
              size="small"
              InputProps={{ sx: { backgroundColor: "white", borderRadius: 2 } }}
            />
          </Stack>
        </LocalizationProvider>
      </DialogContent>

      <DialogActions sx={{ justifyContent: "space-between", px: 2, pb: 2 }}>
        <Button onClick={onClose} disabled={saving} sx={{ color: "#00796b", fontWeight: "bold" }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{ backgroundColor: "#00796b", fontWeight: "bold", "&:hover": { backgroundColor: "#004d40" } }}
        >
          {saving ? "Sending..." : "Send"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}